import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { isEmpty } from 'lodash';
import Text from '../../../../../../Components/Base/Text/Text';
import Input from '../../../../../../Components/Form/components/Input';
import NumberField from '../../../../../../Components/Form/components/NumberField';
import SelectField from '../../../../../../Components/Form/components/SelectField';
import TopFieldContent from '../../../../../../Components/Form/components/TopFieldContent';
import FormContainer from '../../../../../../Components/Form/FormContainer';
import { FieldNames } from '../../../../../../Services/HealthBabyPlanDataContext';
import useTranslation from '../../../../../../Hooks/useTranslation';
//@ts-ignore
import styles from './MothersHealthFields.module.scss';
import { UseFormReturn } from 'react-hook-form';
import QuestionnaireResultSelection from '../QuestionnaireResultSelection';
import TextField from '../../../../../../Components/Base/TextField/TextField';
import { getDateDifference, getDateDifferenceMoment } from '../../utils/date';
import { useInitialQuestionnaireResultQuery } from '../../../../../../graphql/graphql';
import { HealthPlanCode } from '../../../../../../Enums/HealthPlanCode.enum';
import { getMomentDate } from '../../../../../../Utils/dates.util';
import DatePicker from '../../../../../../Components/Base/DatePicker/DatePicker';
import FormControl from '../../../../../../Components/Form/FormControl';
import TimePicker from '../../../../../../Components/Base/TimePicker/TimePicker';
import moment from 'moment';

type ITranslation = (
	key: string | undefined,
	params?: any,
) => string | undefined;

const getOptions = (t: ITranslation) => [
	{ value: 'negative', label: t('base.negative') ?? '' },
	{ value: '+', label: '+' },
	{ value: '++', label: '++' },
	{ value: '+++', label: '+++' },
];

const VALIDATION_TRANSLATION_PREFIX = 'validation.mothersHealthForm';

const getSelectFieldValidation = (validationMessage?: string) =>
	yup
		.object()
		.test('object-check', validationMessage ?? '', score => !isEmpty(score))
		.nullable();

const SWELLING_OPTIONS = [
	{
		value: 'hands',
		label: 'component.mothersHealthFields.hands',
	},
	{
		value: 'legs',
		label: 'component.mothersHealthFields.legs',
	},
	{
		value: 'face',
		label: 'component.mothersHealthFields.face',
	},
	{
		value: 'fullBody',
		label: 'component.mothersHealthFields.fullBody',
	},
	{
		value: 'absent',
		label: 'component.mothersHealthFields.absent',
	},
];

const CTG_OPTIONS = [
	{
		value: 'physiologicalRecord',
		label: 'base.physiologicalRecord',
	},
	{
		value: 'suspiciousRecord',
		label: 'base.suspiciousRecord',
	},
	{
		value: 'pathologicalRecord',
		label: 'base.pathologicalRecord',
	},
	{
		value: 'notDone',
		label: 'base.notDone',
	},
];

export const getMothersHealthFieldsValidationSchema = (
	t: (key: string) => string | undefined,
	isEntryQuestionnaire = false,
) =>
	yup.object({
		[FieldNames.MOTHERS_HEALTH]: yup.object().shape({
			examinationType: isEntryQuestionnaire
				? yup.object()
				: yup
						.object()
						.nullable()
						.test(
							'object-check',
							t(`${VALIDATION_TRANSLATION_PREFIX}.examinationType`) ?? '',
							score => !isEmpty(score),
						),
			weighBeforePregnancy: isEntryQuestionnaire
				? yup
						.string()
						.required(
							t(`${VALIDATION_TRANSLATION_PREFIX}.weighBeforePregnancy`),
						)
						.nullable()
				: yup.string(),
			actualWeight: yup
				.string()
				.required(t(`${VALIDATION_TRANSLATION_PREFIX}.actualWeight`))
				.nullable(),
			additionalWeightInPregnancy: yup.string().nullable(),
			systolicBloodPressure: yup
				.string()
				.required(t(`${VALIDATION_TRANSLATION_PREFIX}.systolicBloodPressure`))
				.nullable(),
			diastolicBloodPressure: yup
				.string()
				.required(t(`${VALIDATION_TRANSLATION_PREFIX}.diastolicBloodPressure`)),
			ctg: getSelectFieldValidation(t(`${VALIDATION_TRANSLATION_PREFIX}.ctg`)),
			edemas: yup
				.array()
				.of(getSelectFieldValidation())
				.nullable()
				.required(t(`${VALIDATION_TRANSLATION_PREFIX}.edemas`))
				.min(1, t(`${VALIDATION_TRANSLATION_PREFIX}.edemas`)),
			urine: yup.object({
				protein: getSelectFieldValidation(
					t(`${VALIDATION_TRANSLATION_PREFIX}.urineProtein`),
				),
				glucose: getSelectFieldValidation(
					t(`${VALIDATION_TRANSLATION_PREFIX}.urineGlucose`),
				),
			}),
		}),
	});

interface Props {
	additionalPregnancyWeight: string;
	title?: string;
	isEntryQuestionnaire?: boolean;
	formMethods: UseFormReturn<any>;
	patientId?: string;
	showAllFields?: boolean;
}

const MothersHealthFields: React.FC<Props> = ({
	additionalPregnancyWeight,
	title = 'base.mothersHealth',
	isEntryQuestionnaire = false,
	formMethods,
	patientId,
	showAllFields = true,
}) => {
	const [lastExaminationType, setLastExaminationType] = useState('');

	const { t } = useTranslation();

	const { data, isLoading } = useInitialQuestionnaireResultQuery({
		userId: patientId,
		healthPlanCode: HealthPlanCode.BABY,
	});

	const { initialQuestionnaireResult } = data?.healthPlanUser ?? {};

	const rows = initialQuestionnaireResult?.questionnaireResultItems ?? [];

	const plannedDateOfBirth = useMemo(() => {
		return rows.find(
			(item): any =>
				item?.question?.key === 'currentPregnancy.plannedDateOfBirth',
		)?.valueJson?.rawValue;
	}, [rows]);

	const dateOfExamination = formMethods.watch(
		`${FieldNames.MOTHERS_HEALTH}.dateOfExamination`,
	);

	const { weeks, days } = useMemo(() => {
		return getDateDifferenceMoment({
			estimatedDateOfBirth: getMomentDate(plannedDateOfBirth),
			dateOfBirth: getMomentDate(dateOfExamination),
		});
	}, [dateOfExamination]);

	const setWeekOfPregnancy = () => {
		formMethods.setValue(
			`${FieldNames.MOTHERS_HEALTH}.weekOfPregnancy`,
			weeks && days ? `${weeks} + ${days}` : '',
		);
	};

	useEffect(() => {
		setWeekOfPregnancy();
	}, [weeks, days]);

	const examinationType = formMethods.watch(
		`${FieldNames.MOTHERS_HEALTH}.examinationType`,
	);

	const handleQuestionnaireResultItemSelect = (results: any) => {
		if (results.mothersActualWeight) {
			formMethods.setValue(
				`${FieldNames.MOTHERS_HEALTH}.actualWeight`,
				results.mothersActualWeight,
			);
		}
		if (results.mothersSystolicBloodPressure) {
			formMethods.setValue(
				`${FieldNames.MOTHERS_HEALTH}.systolicBloodPressure`,
				results.mothersSystolicBloodPressure,
			);
		}
		if (results.mothersDiastolicBloodPressure) {
			formMethods.setValue(
				`${FieldNames.MOTHERS_HEALTH}.diastolicBloodPressure`,
				results.mothersDiastolicBloodPressure,
			);
		}
		if (results.mothersSwelling) {
			const swelling: any[] = [];

			results.mothersSwelling.map((item: string) => {
				// @ts-ignore
				const found = SWELLING_OPTIONS.find(({ value }) => value === item);

				if (!found) {
					return null;
				}

				swelling.push({
					...found,
					label: t(found.label),
				});
			});

			formMethods.setValue(`${FieldNames.MOTHERS_HEALTH}.edemas`, swelling);
		} else {
			formMethods.setValue(`${FieldNames.MOTHERS_HEALTH}.edemas`, [
				{
					value: 'absent',
					label: t('base.absent'),
				},
			]);
		}

		if (typeof results.chemicalExaminationProtein !== 'undefined') {
			const option = getOptions(t).find(
				({ value }) => value === results.chemicalExaminationProtein,
			);
			// @ts-ignore
			formMethods.setValue(
				`${FieldNames.MOTHERS_HEALTH}.urine.protein`,
				option,
			);
		}

		if (typeof results.chemicalExaminationGlucose !== 'undefined') {
			const option = getOptions(t).find(
				({ value }) => value === results.chemicalExaminationGlucose,
			);
			// @ts-ignore
			formMethods.setValue(
				`${FieldNames.MOTHERS_HEALTH}.urine.glucose`,
				option,
			);
		}

		const ctgField = CTG_OPTIONS.find(({ value }) => value === 'notDone');
		if (ctgField) {
			formMethods.setValue(`${FieldNames.MOTHERS_HEALTH}.ctg`, {
				...ctgField,
				label: t(ctgField.label),
			});
		}
	};

	useEffect(() => {
		setLastExaminationType(examinationType?.value);
		if (
			examinationType?.value === 'present' &&
			lastExaminationType != 'present'
		) {
			formMethods.reset({
				[`${FieldNames.MOTHERS_HEALTH}.examinationType`]: {
					value: 'present',
					label: t('base.examinationType.present') ?? '',
				},
			});
			setWeekOfPregnancy();
		}
	}, [examinationType]);

	useEffect(() => {
		formMethods.setValue(
			`${FieldNames.MOTHERS_HEALTH}.dateOfExamination`,
			moment(),
		);
		formMethods.setValue(
			`${FieldNames.MOTHERS_HEALTH}.timeOfExamination`,
			moment(),
		);
	}, [showAllFields]);

	return (
		<>
			<h3 className={styles.title}>
				<Text tx={title} firstUpper />
			</h3>
			<FormContainer name={`${FieldNames.MOTHERS_HEALTH}`}>
				{!isEntryQuestionnaire && (
					<>
						<SelectField
							isRequired
							name='examinationType'
							label='base.examinationType.title'
							options={[
								{
									value: 'present',
									label: t('base.examinationType.present') ?? '',
								},
								{
									value: 'remote',
									label: t('base.examinationType.remote') ?? '',
								},
							]}
							className={styles.field}
						/>
						{patientId && examinationType?.value === 'remote' && (
							<QuestionnaireResultSelection
								patientId={patientId}
								questionnaireCode='meddi_baby_czech_patient'
								onItemSelect={handleQuestionnaireResultItemSelect}
							/>
						)}
					</>
				)}
				{showAllFields && (
					<>
						<div className={styles.dateAndTimeWrapper}>
							<FormControl
								name='dateOfExamination'
								label='base.dateOfExamination'
								isRequired>
								<DatePicker />
							</FormControl>
							<FormControl
								name='timeOfExamination'
								label='base.timeOfExamination'
								isRequired>
								<TimePicker />
							</FormControl>
						</div>
						<div className={styles.weightDetails}>
							{isEntryQuestionnaire && (
								<NumberField
									isRequired
									label='base.weighBeforePregnancy'
									name='weighBeforePregnancy'
									suffix='kg'
									className={styles.weightField}
								/>
							)}
							<NumberField
								isRequired
								label='base.actualWeight'
								name='actualWeight'
								suffix='kg'
								className={styles.weightField}
							/>
							<div>
								<TopFieldContent
									label='base.additionalWeightInPregnancy'
									isRequired
								/>
								<Input
									disabled
									name='additionalWeightInPregnancy'
									suffix='kg'
									className={styles.weightField}
									value={additionalPregnancyWeight}
								/>
							</div>
							{!isEntryQuestionnaire && (
								<div>
									<TextField
										//@ts-ignore
										disabled
										label='base.gestationalWeek'
										name='weekOfPregnancy'
										value={weeks && days ? `${weeks} + ${days}` : ''}
									/>
								</div>
							)}
						</div>
						<Text
							tx='base.bloodPressure'
							className={styles.requiredFields}
							firstUpper
						/>
						<div className={styles.pressureWrapper}>
							<div className={styles.unitWrapper}>
								<div className={styles.unitDescription}>
									<Text tx='base.sys' className={styles.unitTitle} />
									<Text tx='mmHg' className={styles.unit} />
								</div>
								<NumberField isRequired name='systolicBloodPressure' />
							</div>
							<div className={styles.slashWrapper}>
								<Text>/</Text>
							</div>
							<div className={styles.unitWrapper}>
								<div className={styles.unitDescription}>
									<Text tx='base.dia' className={styles.unitTitle} />
									<Text tx='mmHg' className={styles.unit} />
								</div>
								<NumberField isRequired name='diastolicBloodPressure' />
							</div>
						</div>
						<SelectField
							isRequired
							className={styles.field}
							label='base.ctg'
							name='ctg'
							options={CTG_OPTIONS}
						/>
						<SelectField
							isRequired
							label='base.edemas'
							name='edemas'
							className={styles.field}
							isMulti
							options={SWELLING_OPTIONS}
						/>
						<FormContainer name='urine'>
							<SelectField
								isRequired
								label='base.urine.glucose'
								className={styles.field}
								name='glucose'
								shouldTranslateValues={false}
								options={getOptions(t)}
							/>
							<SelectField
								isRequired
								label='base.urine.protein'
								className={styles.field}
								name='protein'
								shouldTranslateValues={false}
								options={getOptions(t)}
							/>
						</FormContainer>
					</>
				)}
			</FormContainer>
		</>
	);
};

export default MothersHealthFields;
